
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, Save, Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { getUserTimezone, getTimezoneAbbreviation } from '@/utils/timezoneUtils';
import { Badge } from '@/components/ui/badge';
import { 
  createOrUpdateAvailability, 
  createOrUpdateBookingPreferences, 
  getMentorAvailability, 
  getMentorBookingPreferences,
  AvailabilitySlot,
  BookingPreferences
} from '@/utils/availabilityUtils';

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

const AvailabilityManager = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [preferences, setPreferences] = useState<BookingPreferences>({
    session_duration: 60,
    buffer_time: 15,
    advance_booking_days: 30,
    timezone: getUserTimezone(), // Auto-detect browser timezone
    auto_approve_bookings: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // Load availability
      const availabilityResult = await getMentorAvailability(user.id);
      if (availabilityResult.success && availabilityResult.data) {
        setAvailability(availabilityResult.data);
      }

      // Load preferences
      const preferencesResult = await getMentorBookingPreferences(user.id);
      const browserTimezone = getUserTimezone();

      if (preferencesResult.success && preferencesResult.data) {
        const loadedPrefs = preferencesResult.data;
        
        // Always sync timezone with browser's current timezone
        if (loadedPrefs.timezone !== browserTimezone) {
          console.log(`Updating timezone from ${loadedPrefs.timezone} to ${browserTimezone}`);
          loadedPrefs.timezone = browserTimezone;
          
          // Auto-save the updated timezone to database
          await createOrUpdateBookingPreferences(user.id, loadedPrefs);
          
          toast({
            title: "Timezone Updated",
            description: `Your timezone has been updated to ${browserTimezone}`,
          });
        }
        
        setPreferences(loadedPrefs);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTimeSlot = (dayOfWeek: number) => {
    const newSlot: AvailabilitySlot = {
      day_of_week: dayOfWeek,
      start_time: '09:00',
      end_time: '17:00',
      is_active: true
    };
    setAvailability(prev => [...prev, newSlot]);
  };

  const updateTimeSlot = (index: number, field: keyof AvailabilitySlot, value: any) => {
    setAvailability(prev => 
      prev.map((slot, i) => 
        i === index ? { ...slot, [field]: value } : slot
      )
    );
  };

  const removeTimeSlot = (index: number) => {
    setAvailability(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!user?.id) return;

    setSaving(true);
    try {
      // Save availability
      const availabilityResult = await createOrUpdateAvailability(user.id, availability);
      if (!availabilityResult.success) {
        throw new Error('Failed to save availability');
      }

      // Save preferences
      const preferencesResult = await createOrUpdateBookingPreferences(user.id, preferences);
      if (!preferencesResult.success) {
        throw new Error('Failed to save preferences');
      }

      toast({
        title: "Settings Saved",
        description: "Your availability and booking preferences have been updated.",
      });
    } catch (error) {
      console.error('Error saving:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save your settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="w-8 h-8 border-4 border-emu-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Booking Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">📍</span>
              <p className="text-sm font-semibold text-gray-900">
                Your Timezone: {preferences.timezone}
              </p>
              <Badge variant="secondary" className="text-xs">
                {getTimezoneAbbreviation(preferences.timezone)}
              </Badge>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              All availability times you set will be in this timezone. Mentees will see these times automatically converted to their local timezone.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="session-duration">Session Duration (minutes)</Label>
              <Input
                id="session-duration"
                type="number"
                value={preferences.session_duration}
                onChange={(e) => setPreferences(prev => ({ 
                  ...prev, 
                  session_duration: parseInt(e.target.value) || 60 
                }))}
                min="15"
                max="180"
              />
            </div>

            <div>
              <Label htmlFor="buffer-time">Buffer Time (minutes)</Label>
              <Input
                id="buffer-time"
                type="number"
                value={preferences.buffer_time}
                onChange={(e) => setPreferences(prev => ({ 
                  ...prev, 
                  buffer_time: parseInt(e.target.value) || 15 
                }))}
                min="0"
                max="60"
              />
            </div>

            <div>
              <Label htmlFor="advance-booking">Advance Booking (days)</Label>
              <Input
                id="advance-booking"
                type="number"
                value={preferences.advance_booking_days}
                onChange={(e) => setPreferences(prev => ({ 
                  ...prev, 
                  advance_booking_days: parseInt(e.target.value) || 30 
                }))}
                min="1"
                max="365"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="auto-approve"
              checked={preferences.auto_approve_bookings}
              onCheckedChange={(checked) => setPreferences(prev => ({ 
                ...prev, 
                auto_approve_bookings: checked 
              }))}
            />
            <Label htmlFor="auto-approve">Auto-approve booking requests</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Weekly Availability</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {DAYS_OF_WEEK.map((dayName, dayOfWeek) => {
            const daySlots = availability.filter(slot => slot.day_of_week === dayOfWeek);
            
            return (
              <div key={dayOfWeek} className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">{dayName}</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addTimeSlot(dayOfWeek)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Time Slot
                  </Button>
                </div>

                {daySlots.length === 0 ? (
                  <p className="text-gray-500 text-sm">No availability set</p>
                ) : (
                  <div className="space-y-2">
                    {daySlots.map((slot, slotIndex) => {
                      const globalIndex = availability.findIndex(s => 
                        s.day_of_week === dayOfWeek && 
                        s.start_time === slot.start_time && 
                        s.end_time === slot.end_time
                      );
                      
                      return (
                        <div key={slotIndex} className="flex items-center gap-3 p-3 border rounded-lg">
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={slot.is_active}
                              onCheckedChange={(checked) => updateTimeSlot(globalIndex, 'is_active', checked)}
                            />
                          </div>
                          
                          <Input
                            type="time"
                            value={slot.start_time}
                            onChange={(e) => updateTimeSlot(globalIndex, 'start_time', e.target.value)}
                            className="w-32"
                          />
                          
                          <span>to</span>
                          
                          <Input
                            type="time"
                            value={slot.end_time}
                            onChange={(e) => updateTimeSlot(globalIndex, 'end_time', e.target.value)}
                            className="w-32"
                          />
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => removeTimeSlot(globalIndex)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full">
        <Save className="h-4 w-4 mr-2" />
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
};

export default AvailabilityManager;
